// The purpose of this middleware is to just add a property "asyncDispatch" to all actions.
//
// The asyncDispatch property can be used in a reducer to asynchronously dispatch another action. When the
// reducer has completed and returned the new state, asyncDispatch will trigger store.dispatch with whatever
// action you gave it.

import type { Middleware, UnknownAction } from '@reduxjs/toolkit';

/** Dispatches an action once the reducer handling the current action has returned. */
export type AsyncDispatch = (action: UnknownAction) => void;

/** Narrows an action to one carrying the middleware's asyncDispatch, which direct reducer calls omit. */
export const hasAsyncDispatch = <A extends object>(action: A): action is A & { asyncDispatch: AsyncDispatch } =>
  'asyncDispatch' in action && typeof action.asyncDispatch === 'function';

const asyncDispatchMiddleware: Middleware = (store) => (next) => (action) => {
  let syncActivityFinished = false;
  let actionQueue: UnknownAction[] = [];

  function flushQueue() {
    actionQueue.forEach((queuedAction) => store.dispatch(queuedAction));
    actionQueue = [];
  }

  const asyncDispatch: AsyncDispatch = (asyncAction) => {
    actionQueue = actionQueue.concat([asyncAction]);

    if (syncActivityFinished) {
      flushQueue();
    }
  };

  const actionWithAsyncDispatch = Object.assign({}, action, { asyncDispatch });

  const returnValue = next(actionWithAsyncDispatch);
  syncActivityFinished = true;
  flushQueue();

  return returnValue;
};

export default asyncDispatchMiddleware;
